/**
 * Account → Sync (web.md §4; sync.md "Host duties", §11): the sign-in state with Sign in / Sign out, the
 * sync status with Sync now, and the account's devices (this one marked; any other can be revoked).
 *
 * Sync starts by itself after sign-in on a plan with sync. When it is off on such a plan — most often
 * because this browser was last synced with ANOTHER account, which the boot refresh leaves for the user
 * to decide — "Turn on sync" calls `startSync`; a `conflict` answer offers merge or replace
 * (`startSync(session, 'merge' | 'replace')`). On Free the area is shown with a Pro mark.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { DeviceInfo, DigesterError, Entitlement, SyncStatus } from '@digester/core';
import { openDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { useServices } from '../../app/services.js';
import { showToast } from '../../app/Toast.js';
import { useEngine, useEngineEvent, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { signOut, startSync } from '../../platform/session.js';
import { Section } from '../../ui/form.js';
import { ProMark, formatDateTime, useAccountState } from '../../account/shared.js';
import { EditorFrame } from '../shell/index.js';

const DEVICES_KEY = ['sync', 'devices'] as const;

/** One line for the sync status. "Syncing" while anything is waiting to go up or is moving. */
export function syncLabel(status: SyncStatus | undefined): string {
  if (!status) return 'Loading…';
  switch (status.phase) {
    case 'off':
      return 'Off';
    case 'pushing':
    case 'pulling':
      return 'Syncing…';
    case 'offline':
      return 'Waiting for the server';
    case 'error':
      return status.error?.message ?? 'Sync needs attention';
    case 'resync':
      return 'Downloading everything again…';
    case 'idle':
      return status.pending > 0 ? `Syncing · ${status.pending} ${status.pending === 1 ? 'change' : 'changes'} waiting` : 'Up to date';
  }
}

function platformIcon(platform: DeviceInfo['platform']): string {
  return platform === 'android' || platform === 'ios' ? 'smartphone' : 'monitor';
}

export function SyncEditor({ entitlement }: { entitlement: Entitlement }) {
  const engine = useEngine();
  const { session } = useServices();
  const account = useAccountState();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const queryClient = useQueryClient();
  const entitled = entitlement.limits.syncDevices > 0;
  const status = useEngineQuery(qk.sync(), (e) => e.sync.status(), { refetchOnMount: 'always' });
  const running = account.signedIn && status.data !== undefined && status.data.phase !== 'off';
  const devices = useQuery<DeviceInfo[], DigesterError>({ queryKey: DEVICES_KEY, queryFn: () => engine.sync.devices(), enabled: running, refetchOnMount: 'always' });
  const fromSignIn = (routerLocation.state as { syncConflict?: boolean } | null)?.syncConflict === true;
  const [conflict, setConflict] = useState(fromSignIn);
  const [busy, setBusy] = useState(false);

  // A local edit raises `pending` without a sync event: re-read the status on every local change.
  useEngineEvent((event) => {
    if (event.type === 'changed' && event.origin !== 'sync') void queryClient.invalidateQueries({ queryKey: qk.sync(), exact: true });
    if (event.type === 'sync' && event.status.phase === 'idle') void queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
  });

  const turnOn = async (onOtherAccount?: 'merge' | 'replace'): Promise<void> => {
    setBusy(true);
    const result = await startSync(session, onOtherAccount);
    setBusy(false);
    setConflict(result.status === 'conflict');
    await queryClient.invalidateQueries({ queryKey: qk.sync(), exact: true });
    if (result.status === 'started') {
      await queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
      showToast('Sync is on');
    } else if (result.status === 'device_limit' || result.status === 'error') showToast(result.error.message);
  };

  const syncNow = async (): Promise<void> => {
    setBusy(true);
    try {
      queryClient.setQueryData(qk.sync(), await engine.sync.now());
      await queryClient.invalidateQueries({ queryKey: DEVICES_KEY });
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const revoke = (device: DeviceInfo): void => {
    openDialog('confirm', {
      title: 'Revoke device',
      message: `Stop syncing ${device.name}?`,
      note: 'That device keeps its data but no longer syncs with this account until it signs in again.',
      confirmLabel: 'Revoke',
      onConfirm: async () => {
        try {
          queryClient.setQueryData(DEVICES_KEY, await engine.sync.revokeDevice(device.id));
          showToast(`${device.name} revoked`);
        } catch (error) {
          reportError(error);
        }
      },
    });
  };

  const doSignOut = (): void => {
    openDialog('confirm', {
      title: 'Sign out',
      message: `Sign out of ${account.email ?? 'this account'}?`,
      note: 'Everything stays in this browser as a Free workspace. Sync stops until you sign in again.',
      confirmLabel: 'Sign out',
      onConfirm: async () => {
        try {
          await signOut(session);
          setConflict(false);
          showToast('Signed out · this browser keeps its data');
        } catch (error) {
          reportError(error);
        }
      },
    });
  };

  return (
    <EditorFrame title="Sync" icon="refresh-cw" status={running ? { label: status.data?.phase === 'error' ? 'Error' : 'On', on: status.data?.phase !== 'error' } : { label: 'Off', on: false }}>
      <Section title="Sign-in">
        {account.signedIn ? (
          <>
            <div className="read-only-row" data-signed-in>
              <span>Signed in as</span>
              <strong>{account.email}</strong>
            </div>
            <div className="panel-actions">
              <button type="button" className="secondary-button" onClick={doSignOut}>
                <Icon name="log-out" /> Sign out
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="field-help panel-lead">Not signed in. Sign in to sync this workspace between your devices and to subscribe; everything else works without an account.</p>
            <div className="panel-actions">
              <button type="button" className="primary-button" onClick={() => navigate('/signin?next=/settings/account/sync')}>
                <Icon name="log-in" /> Sign in
              </button>
            </div>
          </>
        )}
      </Section>
      <Section title={<>Sync{entitled ? null : <ProMark title="Sync between devices is included in Pro" />}</>}>
        {!entitled ? (
          <>
            <p className="field-help panel-lead">Sync keeps this workspace the same on every device you use. It is part of Pro.</p>
            <div className="panel-actions">
              <button type="button" className="secondary-button" onClick={() => navigate('/subscribe')}>
                More
              </button>
            </div>
          </>
        ) : !account.signedIn ? (
          <p className="field-help panel-lead">Sign in to turn on sync.</p>
        ) : (
          <>
            <div className="read-only-row">
              <span>Status</span>
              <strong data-sync-status>{syncLabel(status.data)}</strong>
            </div>
            <div className="read-only-row">
              <span>Last synced</span>
              <strong>{formatDateTime(status.data?.lastSyncedAt)}</strong>
            </div>
            {conflict ? (
              <div className="editor-note sync-choice" role="alert" data-sync-conflict>
                This browser was last synced with another account. Merge its data into {account.email}, or replace it with that account’s data.
                <div className="panel-actions">
                  <button type="button" className="secondary-button" disabled={busy} onClick={() => void turnOn('merge')}>
                    Merge into this account
                  </button>
                  <button type="button" className="secondary-button" disabled={busy} onClick={() => void turnOn('replace')}>
                    Replace with this account’s data
                  </button>
                </div>
              </div>
            ) : null}
            <div className="panel-actions">
              {running ? (
                <button type="button" className="secondary-button" disabled={busy} onClick={() => void syncNow()}>
                  <Icon name="refresh-cw" /> Sync now
                </button>
              ) : (
                <button type="button" className="primary-button" disabled={busy} onClick={() => void turnOn()}>
                  <Icon name="refresh-cw" /> Turn on sync
                </button>
              )}
            </div>
          </>
        )}
      </Section>
      {running ? (
        <Section title="Devices">
          <div className="device-list">
            {(devices.data ?? []).map((d) => (
              <div className="device-row" key={d.id} data-device={d.id} data-current={d.current ? 'true' : undefined}>
                <Icon name={platformIcon(d.platform)} />
                <span>
                  <strong>{d.name}</strong>
                  <small>Last synced {formatDateTime(d.lastSyncAt)}</small>
                </span>
                {d.current ? (
                  <span className="status-label enabled">This device</span>
                ) : (
                  <button type="button" className="secondary-button" onClick={() => revoke(d)}>
                    Revoke
                  </button>
                )}
              </div>
            ))}
            {devices.error ? <p className="field-help">{devices.error.message}</p> : null}
            {!devices.data && !devices.error ? <p className="field-help">Loading…</p> : null}
          </div>
        </Section>
      ) : null}
    </EditorFrame>
  );
}
